import React, { useEffect, useState, useRef } from "react";
import TdLink from "../../components/TdLink";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEllipsis,
    faPenToSquare,
    faCircleMinus,
    faPlus,
    faArrowDownShortWide,
    faArrowDownWideShort,
    faSearch
} from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { getManufacturers, deleteManufacturer } from "../../api/Manufacturer";
import { useAuth } from "../../providers/AuthContext";
import CustomDropdown from "../../components/CustomDropdown";
import InputField from "../../components/InputField";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import { useTranslation } from "react-i18next";

const ManufacturerPage = () => {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const [manufacturers, setManufacturers] = useState([]);
    const [filteredManufacturers, setFilteredManufacturers] = useState([]);
    const [sortColumn, setSortColumn] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");
    const [search, setSearch] = useState("");
    const [manufacturersLoaded, setManufacturersLoaded] = useState(false);
    const [confirmState, setConfirmState] = useState({
        isOpen: false,
        title: "",
        confirm: () => { }
    });

    const fetched = useRef(false);

    useEffect(() => {
        if (fetched.current) return;
        fetched.current = true;
        handleGetManufacturers();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, []);

    useEffect(() => {
        const filtered = (() => {
            let list = manufacturers;

            if (search) {
                const q = search.toLowerCase();
                list = list.filter((m) =>
                    Object.values(m).some((v) => v?.toString().toLowerCase().includes(q))
                );
            }

            if (sortColumn) {
                list = [...list].sort((a, b) => {
                    const av = a[sortColumn]?.toString().toLowerCase() ?? "";
                    const bv = b[sortColumn]?.toString().toLowerCase() ?? "";
                    return sortOrder === "asc" ? (av > bv ? 1 : -1) : av < bv ? 1 : -1;
                });
            }

            return list;
        })();

        setFilteredManufacturers(filtered);
    }, [search, manufacturers, sortColumn, sortOrder]);

    const handleGetManufacturers = async () => {
        try {
            const accessToken = await getAccessToken();
            const newManufacturers = await getManufacturers(accessToken);
            setManufacturers(newManufacturers);
            setFilteredManufacturers(newManufacturers);
            setManufacturersLoaded(true);
        } catch (error) {
            console.error(error);
        }
    };

    const handleDeleteManufacturer = async (manufacturerPublicId) => {
        const toastId = toast.loading(t("toast.deleting", { entity: t("entity.manufacturer") }));
        try {
            const accessToken = await getAccessToken();
            await deleteManufacturer(manufacturerPublicId, accessToken);
            const newManufacturers = await getManufacturers(accessToken);
            setManufacturers(newManufacturers);
            toast.success(t("toast.deleted", { entity: t("entity.manufacturer") }), { id: toastId });
        } catch (error) {
            toast.error(t("toast.failedDelete", { entity: t("entity.manufacturer") }), { id: toastId });
            console.error(error);
        }
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortOrder("asc");
        }
    };

    const getSortIcon = (column) => {
        if (sortColumn === column) {
            return sortOrder === "asc" ? faArrowDownShortWide : faArrowDownWideShort;
        }
        return null;
    };

    return (
        <>
            <HeaderBar />
            <div className="flex flex-col">
                <div className="flex flex-row">
                    <Sidebar />
                    <main className="page">
                        <div className="absolute inset-0 pointer-events-none">
                            <LoadingTablePage
                                title={t("entity.manufacturers")}
                                loading={!manufacturersLoaded}
                            />
                        </div>

                        <div className={`${!manufacturersLoaded ? "loading" : ""} page-content`}>
                            <div className="flex justify-between items-center">
                                <h2 className="text-2xl text-black font-bold pb-2">{t("entity.manufacturers")}</h2>
                                <NavLink className="btn btn-primary" to={`/manufacturer/create`}>
                                    <FontAwesomeIcon icon={faPlus} /> {t("pages.createManufacturer")}
                                </NavLink>
                            </div>

                            <div className="rounded-md shadow-md w-full p-4">
                                <div className="flex mb-2 gap-4 justify-end">
                                    <div className="flex items-center">
                                        <InputField
                                            icon={faSearch}
                                            placeholder={t("common.search")}
                                            value={search}
                                            onChange={(v) => setSearch(v.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                                    <table className="table-default w-full">
                                        <thead>
                                            <tr>
                                                <th
                                                    className="text-left cursor-pointer"
                                                    onClick={() => handleSort("manufacturerPublicId")}
                                                >
                                                    {t("table.manufacturerId")}
                                                    {getSortIcon("manufacturerPublicId") && (
                                                        <FontAwesomeIcon
                                                            icon={getSortIcon("manufacturerPublicId")}
                                                            className="w-4 pl-2"
                                                        />
                                                    )}
                                                </th>

                                                <th
                                                    className="text-left cursor-pointer"
                                                    onClick={() => handleSort("manufacturerName")}
                                                >
                                                    {t("common.name")}
                                                    {getSortIcon("manufacturerName") && (
                                                        <FontAwesomeIcon
                                                            icon={getSortIcon("manufacturerName")}
                                                            className="w-4 pl-2"
                                                        />
                                                    )}
                                                </th>

                                                <th></th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {filteredManufacturers.length > 0 ? (
                                                filteredManufacturers.map((manufacturer, index) => (
                                                    <tr key={index}>
                                                        <TdLink to={`/manufacturer/${manufacturer.manufacturerPublicId}`}>
                                                            {manufacturer.manufacturerPublicId || ""}
                                                        </TdLink>

                                                        <TdLink to={`/manufacturer/${manufacturer.manufacturerPublicId}`}>
                                                            {manufacturer.manufacturerName || ""}
                                                        </TdLink>

                                                        <td className="flex justify-end">
                                                            <CustomDropdown
                                                                isRight={true}
                                                                ContentComponent={() => (
                                                                    <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                                                                        <NavLink
                                                                            className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                                                                            to={`/manufacturer/${manufacturer.manufacturerPublicId}`}
                                                                        >
                                                                            <FontAwesomeIcon icon={faPenToSquare} /> {t("common.edit")}
                                                                        </NavLink>

                                                                        <button
                                                                            className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                                                                            onClick={() =>
                                                                                setConfirmState({
                                                                                    isOpen: true,
                                                                                    title: t("confirm.deleteEntity", {
                                                                                        entity: t("entity.manufacturer"),
                                                                                        name: manufacturer.manufacturerName,
                                                                                    }),
                                                                                    confirm: () =>
                                                                                        handleDeleteManufacturer(manufacturer.manufacturerPublicId)
                                                                                })
                                                                            }
                                                                        >
                                                                            <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            >
                                                                <div className="w-8 h-6 flex justify-end items-center cursor-pointer">
                                                                    <FontAwesomeIcon icon={faEllipsis} />
                                                                </div>
                                                            </CustomDropdown>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="text-center"></td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            <ConfirmationModal
                isOpen={confirmState.isOpen}
                title={confirmState.title}
                onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
                onConfirm={() => {
                    setConfirmState((s) => ({ ...s, isOpen: false }));
                    confirmState.confirm();
                }}
            />
        </>
    );
};

export default ManufacturerPage;
